import React from "react";
import { useNavigate } from "react-router-dom";

interface ServiceFeature {
  title: string;
  description: string;
  icon: JSX.Element;
}

const FabricationServices: React.FC = () => {
  const navigate = useNavigate();

  const features: ServiceFeature[] = [
    {
      title: "Custom Metal Fabrication",
      description:
        "Precision fabrication of custom metal components and structures tailored to your specific requirements.",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      ),
    },
    {
      title: "Sheet Metal Work",
      description:
        "Expert sheet metal fabrication including cutting, bending, and forming for various industrial applications.",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      ),
    },
    {
      title: "Industrial Equipment",
      description:
        "Manufacturing and fabrication of industrial equipment and machinery components to meet your specifications.",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
          />
        </svg>
      ),
    },
  ];

  const capabilities = [
    {
      title: "Precision Cutting",
      items: ["Laser Cutting", "Precision Shearing"],
    },
    {
      title: "Metal Forming",
      items: [
        "Press Brake Forming",
        "Roll Forming",
        "Metal Stamping",
        "Custom Bending",
      ],
    },
    {
      title: "Assembly Services",
      items: [
        "Mechanical Assembly",
        "Structural Assembly",
        "Component Integration",
        "Quality Testing",
      ],
    },
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white py-24">
        <div className="absolute inset-0">
          <img
            src="/about.jpg"
            alt="Metal Fabrication Services"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black opacity-60"></div>
        </div>
        <div className="relative container mx-auto px-4">
          <h1 className="text-5xl font-bold mb-6">
            Metal Fabrication Services
          </h1>
          <p className="text-lg text-gray-300 max-w-3xl">
            State-of-the-art metal fabrication services delivering precision,
            quality, and innovation for your manufacturing needs.
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="text-orange-500 mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">
            Our Fabrication Capabilities
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {capabilities.map((category, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-lg">
                <h3 className="text-xl font-bold mb-4 text-orange-500">
                  {category.title}
                </h3>
                <ul className="space-y-3">
                  {category.items.map((item, idx) => (
                    <li key={idx} className="flex items-center text-gray-600">
                      <svg
                        className="w-5 h-5 text-orange-500 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-3xl font-bold mb-6">Why Choose Us</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <p className="text-gray-600 mb-4">
                  Choose Ekumfi Engineering for your metal fabrication needs:
                </p>
                <ul className="space-y-3">
                  {[
                    "Advanced fabrication technology",
                    "Experienced fabrication team",
                    "Custom solutions for any project",
                    "Competitive pricing",
                    "On-time project delivery",
                    "Quality assurance guarantee",
                  ].map((item, index) => (
                    <li key={index} className="flex items-center text-gray-600">
                      <svg
                        className="w-5 h-5 text-orange-500 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <img
                  src="/quality.jpg"
                  alt="Fabrication Facility"
                  className="rounded-lg shadow-md"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-orange-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Start Your Fabrication Project?
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">
            Contact us today to discuss your metal fabrication requirements. Our
            team is ready to bring your ideas to life.
          </p>
          <div className="flex justify-center space-x-4">
            <button
              onClick={() => navigate("/contacts")}
              className="bg-white text-orange-500 px-8 py-3 rounded hover:bg-gray-100 transition-colors"
            >
              Contact Us
            </button>
            <button
              onClick={() => navigate("/projects")}
              className="border-2 border-white text-white px-8 py-3 rounded hover:bg-white hover:text-orange-500 transition-colors"
            >
              View Projects
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FabricationServices;
